/**
 * Resolved Assets workspace coordinates for a tenant.
 *
 * `host === null` means "stay on the current site" (i.e. the BE indicated the
 * Assets UI lives on the same site the user is currently visiting). Consumers
 * should render relative `/jira/assets/...` links in that case.
 *
 * `host === 'foo.atlassian.net'` means consumers must navigate to that host;
 * use `toAbsoluteUrl` to assemble the redirect URL.
 *
 * `relativePath` is typed as `string` (not the template literal
 * `` `/jira/assets/${string}` ``) so that callers passing a router-derived
 * path don't have to cast on every site. The `/jira/assets/` prefix is
 * enforced at runtime by `buildAbsoluteAssetsUrl`.
 *
 * `cmdbSiteCloudId === null` means the BE did not return a Units-aware Assets
 * site cloud id for this tenant (legacy / non-Units site, or BE rollout not
 * yet enabled). When non-null, consumers can use it as the canonical
 * `cloudId` for any per-tenant Assets API call that must target the Assets
 * site explicitly (rather than the current browsing site).
 */
export type AssetsWorkspaceHost = {
	readonly cmdbSiteCloudId: string | null;
	readonly host: string | null;
	readonly toAbsoluteUrl: (relativePath: string) => string;
	readonly workspaceId: string;
};

export const AssetsWorkspaceHostStatus: Readonly<{
	readonly Error: 'error';
	readonly Idle: 'idle';
	readonly Pending: 'pending';
	readonly Success: 'success';
	readonly Unavailable: 'unavailable';
}> = Object.freeze({
	Idle: 'idle',
	Pending: 'pending',
	Success: 'success',
	Error: 'error',
	Unavailable: 'unavailable',
} as const);

export type AssetsWorkspaceHostStatus =
	(typeof AssetsWorkspaceHostStatus)[keyof typeof AssetsWorkspaceHostStatus];

/**
 * Discriminated union returned by `useAssetsWorkspaceHost`.
 *
 * Designed so consumers can `switch (state.status)` and have TypeScript narrow
 * `data` and `error` automatically — i.e. no invalid combinations are
 * representable. Status values are constrained to the {@link AssetsWorkspaceHostStatus}
 * enum.
 */
export type AssetsWorkspaceHostState =
	| {
			readonly data: null;
			readonly error: null;
			readonly status: typeof AssetsWorkspaceHostStatus.Idle;
	  }
	| {
			readonly data: null;
			readonly error: null;
			readonly status: typeof AssetsWorkspaceHostStatus.Pending;
	  }
	| {
			readonly data: AssetsWorkspaceHost;
			readonly error: null;
			readonly status: typeof AssetsWorkspaceHostStatus.Success;
	  }
	| {
			readonly data: null;
			readonly error: Error;
			readonly status: typeof AssetsWorkspaceHostStatus.Error;
	  }
	| {
			readonly data: null;
			readonly error: null;
			readonly status: typeof AssetsWorkspaceHostStatus.Unavailable;
	  };

/**
 * Wire shape of the BE response, per the WorkspaceLatestResponse contract:
 *
 * ```kotlin
 * data class WorkspaceLatestResponse(
 *   val workspaceId: String,
 *   val site: String? = null,
 *   val cmdbSiteCloudId: String? = null,
 * )
 * ```
 */
export type WorkspaceLatestResponse = {
	readonly cmdbSiteCloudId?: string | null;
	readonly site?: string | null;
	readonly workspaceId: string;
};

import type { ProductContext } from './constants';

export type FetchAssetsWorkspaceHostOptions = {
	/**
	 * Tenant cloud id. Mandatory — every cache entry is keyed by `cloudId`,
	 * so callers must explicitly choose which tenant they are resolving for.
	 * Read it from `useTenantContext()` (or equivalent) at the call site.
	 */
	readonly cloudId: string;
	/** Override the endpoint (e.g. for staging dev hosts). */
	readonly endpoint?: string;
	/** Bypass the in-memory cache and force a network call. */
	readonly force?: boolean;
	/**
	 * When `true`, hits the canonical `/workspace` endpoint (no product
	 * context). When `false` (default), hits `/internal/workspace?productContext={productContext}`.
	 */
	readonly isOnlyWorkspace?: boolean;
	/**
	 * Product context query parameter for the `/internal/workspace` endpoint.
	 * Ignored when `isOnlyWorkspace === true`. Defaults to `'jsm'`.
	 */
	readonly productContext?: ProductContext;
};

export type UseAssetsWorkspaceHostOptions = {
	/**
	 * Tenant cloud id. Mandatory — see {@link FetchAssetsWorkspaceHostOptions.cloudId}.
	 */
	readonly cloudId: string;
	/**
	 * When `true`, hits the canonical `/workspace` endpoint. Defaults to `false`.
	 */
	readonly isOnlyWorkspace?: boolean;
	/**
	 * Product context query parameter for the `/internal/workspace` endpoint.
	 * Ignored when `isOnlyWorkspace === true`. Defaults to `'jsm'`.
	 */
	readonly productContext?: ProductContext;
	/**
	 * When true, the hook returns `status: 'idle'` and does not subscribe to
	 * the resolver. Use for routes/components that already have the value
	 * via some other source (e.g. seeded from SSR or a parent context).
	 */
	readonly skip?: boolean;
};
